import { useState } from "react";
import { api, apiError, ROLE_LABELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, FileWarning, CheckCircle2, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const SAMPLE = `first name,last name,email,company,role,partner tier
James,Whitfield,james.whitfield@britannia.co.uk,Britannia Energy Consultants,partner,Gold
Sarah,Okonkwo,sarah.okonkwo@northgate.co.uk,Northgate Utilities,partner,Silver
David,Patel,david.patel@trueacademy.co.uk,True Academy,internal_staff,
bad-email,Missing,not-an-email,Some Co,partner,Bronze`;

export default function AdminImport() {
  const [content, setContent] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setContent(String(reader.result)); setPreview(null); };
    reader.readAsText(file);
  };

  const runPreview = async () => {
    if (!content.trim()) return toast.error("Paste CSV or choose a file first");
    setLoading(true);
    try {
      const { data } = await api.post("/admin/import/preview", { content });
      setPreview(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const { data } = await api.post("/admin/import/commit", { content });
      toast.success(`${data.created} users imported`);
      setPreview(null);
      setContent("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setCommitting(false); }
  };

  return (
    <div className="space-y-6">
      <div className="card-surface p-6">
        <h2 className="font-heading text-[18px] font-semibold">Bulk import users</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Columns: first name, last name, email, company, role, partner tier. We validate, preview and flag
          duplicates and malformed emails before anything is written.
        </p>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <label className="btn-brand inline-flex h-10 cursor-pointer items-center gap-2 px-4 text-sm" data-testid="csv-file-label">
            <Upload className="h-4 w-4" /> Choose CSV file
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="csv-bulk-import-input" />
          </label>
          <Button variant="outline" className="h-10" onClick={() => { setContent(SAMPLE); setPreview(null); }} data-testid="load-sample-button">
            <Download className="mr-2 h-4 w-4" /> Load sample
          </Button>
        </div>

        <textarea
          data-testid="csv-textarea"
          value={content}
          onChange={(e) => { setContent(e.target.value); setPreview(null); }}
          rows={7}
          placeholder="…or paste CSV content here"
          className="mt-4 w-full rounded-lg border border-input bg-card p-3 font-mono text-xs outline-none focus:ring-2 focus:ring-ring"
        />

        <Button onClick={runPreview} disabled={loading} className="btn-brand mt-4 h-10" data-testid="preview-button">
          {loading ? "Validating…" : "Validate & preview"}
        </Button>
      </div>

      {preview && (
        <div className="card-surface overflow-hidden" data-testid="import-preview">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-5">
            <div className="flex items-center gap-4 text-sm">
              <span className="inline-flex items-center gap-1.5"><span className="tnum font-semibold">{preview.summary.total}</span> rows</span>
              <span className="inline-flex items-center gap-1.5 text-emerald-600"><CheckCircle2 className="h-4 w-4" /><span className="tnum font-semibold">{preview.summary.valid}</span> valid</span>
              <span className="inline-flex items-center gap-1.5 text-amber-600"><AlertTriangle className="h-4 w-4" /><span className="tnum font-semibold">{preview.summary.invalid}</span> flagged</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="h-9" onClick={() => setPreview(null)} data-testid="cancel-import-button">Cancel</Button>
              <Button className="btn-brand h-9" disabled={committing || preview.summary.valid === 0} onClick={commit} data-testid="commit-import-button">
                {committing ? "Importing…" : `Import ${preview.summary.valid} users`}
              </Button>
            </div>
          </div>

          <div className="max-h-[420px] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-secondary/80 text-left text-xs uppercase tracking-wide text-muted-foreground backdrop-blur">
                <tr>
                  <th className="px-4 py-2.5 font-semibold">Name</th>
                  <th className="px-4 py-2.5 font-semibold">Email</th>
                  <th className="px-4 py-2.5 font-semibold">Company</th>
                  <th className="px-4 py-2.5 font-semibold">Role</th>
                  <th className="px-4 py-2.5 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {preview.rows.map((r, i) => (
                  <tr key={i} className={cn(r.issues.length && "bg-amber-50/60 dark:bg-amber-950/20")} data-testid={`preview-row-${i}`}>
                    <td className="px-4 py-2.5">{r.first_name} {r.last_name}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.email || "-"}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{r.company || "-"}</td>
                    <td className="px-4 py-2.5">{ROLE_LABELS[r.role] || r.role}</td>
                    <td className="px-4 py-2.5">
                      {r.issues.length === 0 ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> Ready</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-600"><FileWarning className="h-3.5 w-3.5" /> {r.issues.join(", ")}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
